import { authorizedFetch } from "./auth/auth.ts";
import { config } from "./config.ts";

/*
 * Synchronisation des données de lecture avec le serveur IA (module « reader-data », même contrat que l'app) :
 * position, signets et réglages de lecture suivent le compte d'un appareil à l'autre, et un journal d'événements de
 * lecture alimente le profil du lecteur. Le navigateur reste la source de travail (hors ligne, rien ne bloque) : il
 * garde une copie des éléments, envoie ses modifications quand il peut, puis demande celles des autres appareils
 * après le dernier curseur reçu. Le plus récent gagne ; à égalité, une suppression l'emporte.
 */

export type ItemKind = "location" | "bookmark" | "annotation" | "preferences" | "library";

/** Un élément tel que le serveur le garde. */
export interface SyncItem {
  id: string;
  kind: ItemKind;
  product_id: string | null;
  /** Ce que garde l'appareil (un locator Readium, un signet…) : le serveur n'y lit rien. */
  data: Record<string, unknown>;
  visibility?: "private" | "club" | "followers";
  updated_at: string;
  deleted: boolean;
}

/** Élément gardé ici ; `dirty` tant que le serveur ne l'a pas reçu. */
interface Entry extends SyncItem {
  dirty?: boolean;
}

export type EventType = "book_opened" | "reading_session" | "book_finished" | "book_abandoned" | "annotation_created" | "bookmark_created";

interface PendingEvent {
  id: string;
  type: EventType;
  product_id: string | null;
  occurred_at: string;
  data: Record<string, unknown>;
}

interface State {
  cursor: number;
  deviceId: string;
  items: Record<string, Entry>;
  events: PendingEvent[];
}

interface Page {
  items: SyncItem[];
  version: number;
  more?: boolean;
}

const MAX_BATCH = 500;
// Les événements qu'on n'arrive pas à envoyer ne s'accumulent pas sans fin.
const MAX_PENDING_EVENTS = 500;
const FLUSH_DELAY_MS = 4000;

const keyOf = (kind: ItemKind, id: string) => `${kind}|${id}`;
const time = (value: string) => Date.parse(value);

/** Comme sur le serveur : le plus récent gagne ; à égalité, une suppression l'emporte. */
export function wins(incoming: SyncItem, stored: SyncItem | undefined): boolean {
  if (!stored) return true;
  const a = time(incoming.updated_at);
  const b = time(stored.updated_at);
  if (a !== b) return a > b;
  return incoming.deleted && !stored.deleted;
}

const randomId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export interface SyncOptions {
  /** Clé de stockage : un compte par clé, pour qu'un autre compte du même navigateur ne voie pas ces données. */
  storageKey: string;
  storage?: Storage;
  fetchImpl?: (url: string, init: RequestInit) => Promise<Response>;
  now?: () => Date;
  baseUrl?: string;
}

export class SyncStore {
  private state: State;
  private readonly storage: Storage | undefined;
  private readonly storageKey: string;
  private readonly fetchImpl: (url: string, init: RequestInit) => Promise<Response>;
  private readonly now: () => Date;
  private readonly baseUrl: string;
  private running: Promise<boolean> | undefined;
  private again = false;
  private timer: ReturnType<typeof setTimeout> | undefined;

  constructor(options: SyncOptions) {
    this.storageKey = options.storageKey;
    this.storage = options.storage ?? safeStorage();
    this.fetchImpl =
      options.fetchImpl ??
      ((url, init) =>
        authorizedFetch(url, { ...init, headers: { ...(init.headers as Record<string, string>), "X-Auth-Provider": "reader-application" } }));
    this.now = options.now ?? (() => new Date());
    this.baseUrl = (options.baseUrl ?? `${config.aiApiUrl.replace(/\/+$/, "")}/server/reader-data`).replace(/\/+$/, "");
    this.state = this.load();
  }

  private load(): State {
    try {
      const saved = JSON.parse(this.storage?.getItem(this.storageKey) ?? "null") as Partial<State> | null;
      if (saved && typeof saved === "object") {
        return {
          cursor: typeof saved.cursor === "number" ? saved.cursor : 0,
          deviceId: saved.deviceId || randomId(),
          items: saved.items ?? {},
          events: saved.events ?? [],
        };
      }
    } catch {
      // Copie illisible : on repart de zéro, le serveur redonne tout.
    }
    return { cursor: 0, deviceId: randomId(), items: {}, events: [] };
  }

  private save(): void {
    try {
      this.storage?.setItem(this.storageKey, JSON.stringify(this.state));
    } catch {
      // Stockage indisponible ou plein : la synchro continue en mémoire pour cette session.
    }
  }

  /** L'élément, s'il existe et n'est pas supprimé. */
  get(kind: ItemKind, id: string): SyncItem | undefined {
    const entry = this.state.items[keyOf(kind, id)];
    return entry && !entry.deleted ? entry : undefined;
  }

  /** Vrai si l'élément est connu, même supprimé (une suppression n'est pas à renvoyer comme nouveauté). */
  has(kind: ItemKind, id: string): boolean {
    return keyOf(kind, id) in this.state.items;
  }

  /** Les éléments d'un type pour un livre, non supprimés. */
  list(kind: ItemKind, productId: string): SyncItem[] {
    return Object.values(this.state.items).filter((e) => e.kind === kind && e.product_id === productId && !e.deleted);
  }

  /** Enregistre une modification de cet appareil et programme son envoi. */
  put(kind: ItemKind, id: string, productId: string | null, data: Record<string, unknown>, deleted = false): void {
    this.state.items[keyOf(kind, id)] = {
      id,
      kind,
      product_id: productId,
      data,
      visibility: "private",
      updated_at: this.now().toISOString(),
      deleted,
      dirty: true,
    };
    this.save();
    this.schedule();
  }

  /** Comme `put`, sauf si l'élément a déjà ces données (réglages rouverts sans changement : pas de nouvelle date). */
  putIfChanged(kind: ItemKind, id: string, productId: string | null, data: Record<string, unknown>): void {
    const known = this.get(kind, id);
    if (known && JSON.stringify(known.data) === JSON.stringify(data)) return;
    this.put(kind, id, productId, data);
  }

  /** Ajoute un événement de lecture, envoyé avec la prochaine synchro (une seule fois, grâce à son id). */
  event(type: EventType, productId: string | null, data: Record<string, unknown> = {}): void {
    this.state.events.push({ id: randomId(), type, product_id: productId, occurred_at: this.now().toISOString(), data });
    if (this.state.events.length > MAX_PENDING_EVENTS) this.state.events.splice(0, this.state.events.length - MAX_PENDING_EVENTS);
    this.save();
    this.schedule();
  }

  private schedule(delay = FLUSH_DELAY_MS): void {
    if (this.timer !== undefined) return;
    this.timer = setTimeout(() => {
      this.timer = undefined;
      void this.sync();
    }, delay);
  }

  /** Envoi immédiat, par exemple quand l'onglet se ferme. */
  flush(): Promise<boolean> {
    if (this.timer !== undefined) {
      clearTimeout(this.timer);
      this.timer = undefined;
    }
    return this.sync(true);
  }

  /**
   * Envoie les modifications, puis reçoit celles des autres appareils. Renvoie `false` hors ligne ou sans session :
   * rien n'est perdu, la prochaine synchro reprend.
   */
  sync(keepalive = false): Promise<boolean> {
    if (this.running) {
      this.again = true;
      return this.running;
    }
    this.running = (async () => {
      try {
        do {
          this.again = false;
          await this.push(keepalive);
          await this.pull();
        } while (this.again);
        return true;
      } catch {
        return false;
      } finally {
        this.running = undefined;
      }
    })();
    return this.running;
  }

  private async request<T>(method: string, path: string, body?: unknown, keepalive = false): Promise<T> {
    const response = await this.fetchImpl(`${this.baseUrl}${path}`, {
      method,
      headers: body === undefined ? {} : { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
      keepalive,
    });
    if (!response.ok) throw new Error(`reader-data ${response.status}`);
    return (await response.json()) as T;
  }

  private async push(keepalive: boolean): Promise<void> {
    for (;;) {
      const dirty = Object.values(this.state.items).filter((e) => e.dirty).slice(0, MAX_BATCH);
      if (!dirty.length) break;
      const sent = new Map(dirty.map((e) => [keyOf(e.kind, e.id), e.updated_at]));
      const items = dirty.map(({ dirty: _dirty, ...item }) => item);
      const kept = await this.request<Page>("POST", "/changes", { device_id: this.state.deviceId, items }, keepalive);
      for (const item of kept.items) {
        const key = keyOf(item.kind, item.id);
        const local = this.state.items[key];
        // Modifié ici pendant l'envoi : la nouvelle version reste à envoyer.
        if (local?.dirty && local.updated_at !== sent.get(key)) continue;
        this.state.items[key] = item;
      }
      // Ce que le serveur n'a pas renvoyé est tout de même reçu.
      for (const [key, stamp] of sent) {
        const local = this.state.items[key];
        if (local?.dirty && local.updated_at === stamp) delete local.dirty;
      }
      this.save();
    }
    if (this.state.events.length) {
      const events = this.state.events.slice(0, MAX_BATCH);
      await this.request("POST", "/events", { device_id: this.state.deviceId, events }, keepalive);
      const sentIds = new Set(events.map((e) => e.id));
      this.state.events = this.state.events.filter((e) => !sentIds.has(e.id));
      this.save();
    }
  }

  private async pull(): Promise<void> {
    for (;;) {
      const page = await this.request<Page>("GET", `/changes?since=${this.state.cursor}&limit=${MAX_BATCH}`);
      for (const item of page.items) {
        const key = keyOf(item.kind, item.id);
        const local = this.state.items[key];
        // Une modification d'ici pas encore envoyée, plus récente, garde sa place.
        if (local?.dirty && !wins(item, local)) continue;
        this.state.items[key] = item;
      }
      this.state.cursor = page.version;
      this.save();
      if (!page.more) break;
    }
  }

  /** Une synchro qui n'attend pas plus de `ms` : à l'ouverture d'un livre, la position des autres appareils si elle arrive vite. */
  async syncWithin(ms: number): Promise<void> {
    await Promise.race([this.sync(), new Promise<void>((resolve) => setTimeout(resolve, ms))]);
  }
}

function safeStorage(): Storage | undefined {
  try {
    return localStorage;
  } catch {
    return undefined;
  }
}

/** Compte du jeton (`sub` : id client Shopware), sans vérification : il ne sert qu'à séparer les copies locales. */
export function subjectOf(token: string | undefined): string | undefined {
  try {
    const binary = atob(token!.split(".")[1]!.replace(/-/g, "+").replace(/_/g, "/"));
    const sub = JSON.parse(binary).sub;
    return typeof sub === "string" && sub ? sub : undefined;
  } catch {
    return undefined;
  }
}

const stores = new Map<string, SyncStore>();

/** La synchro du compte connecté ; `undefined` sans session. */
export function syncFor(token: string | undefined): SyncStore | undefined {
  const sub = subjectOf(token);
  if (!sub) return undefined;
  let store = stores.get(sub);
  if (!store) {
    store = new SyncStore({ storageKey: `msb-reader:sync:${sub}` });
    stores.set(sub, store);
  }
  return store;
}

/** Temps de lecture réel d'une ouverture de livre : seulement l'onglet visible, en secondes. */
export class ReadingSession {
  private seconds = 0;
  private since: number | undefined;
  private done = false;

  constructor(
    private readonly sync: SyncStore,
    private readonly productId: string,
    private readonly clock: () => number = () => Date.now(),
    private readonly visible: () => boolean = () => typeof document === "undefined" || document.visibilityState === "visible",
  ) {
    if (this.visible()) this.since = this.clock();
  }

  /** À appeler quand l'onglet apparaît ou disparaît. */
  visibilityChanged(): void {
    if (this.done) return;
    if (this.visible()) this.since ??= this.clock();
    else this.pause();
  }

  private pause(): void {
    if (this.since !== undefined) this.seconds += (this.clock() - this.since) / 1000;
    this.since = undefined;
  }

  /** Clôt la session (une seule fois) ; sous 10 secondes, ce n'est pas de la lecture. */
  end(progression?: number): void {
    if (this.done) return;
    this.done = true;
    this.pause();
    const seconds = Math.round(this.seconds);
    if (seconds < 10) return;
    this.sync.event("reading_session", this.productId, progression === undefined ? { seconds } : { seconds, progression });
  }
}
